import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container, Card, CardContent, Typography, Tabs, Tab, Box,
  Grid, TextField, Checkbox, FormControlLabel, Button,
  Select, MenuItem, FormControl, InputLabel, Divider, Chip,
  List, ListItem, ListItemText, ListItemButton, InputAdornment, IconButton,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import Page from '../../components/Page';
import BackButton from '../../components/BackButton';
import MoneyInput from '../../components/MoneyInput';
import CommentReplyBox from '../../components/CommentReplyBox';
import PrintButton from '../../components/print/PrintButton';
import SacramentPrintTemplate from '../../sections/@dashboard/sacraments/SacramentPrintTemplate';
import OfficeComments from '../../sections/@dashboard/sacraments/OfficeComments';
import tracker from '../../api/tracker';
import useNotification from '../../hooks/useNotificaction';
import { fDateOnly, fDateTime } from '../../utils/formatTime';
import { toTitleCase, formatValue } from '../../utils/formatText';
import { formatAge } from '../../utils/calculateAge';
import PaymentQuotePreview, { fetchSacramentPaymentQuote } from '../../sections/@dashboard/sacraments/PaymentQuotePreview';
import useUnsavedNavigationGuard from '../../hooks/useUnsavedNavigationGuard';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>{value === index && <Box sx={{ pt: 3 }}>{children}</Box>}</div>
);

const CONFIRMATION_SPONSOR_FIELDS = ['sponsor_name', 'sponsor_phone', 'sponsor_email', 'sponsor_religion'];

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value || '—'}</Typography>
  </Grid>
);

const withCurrentOption = (options, current) => {
  if (current && !options.some((option) => option.value === current)) {
    return [{ value: current, label: String(current) }, ...options];
  }
  return options;
};

const toDraftFormData = (formData) => {
  const next = {};
  Object.entries(formData || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) next[key] = value.join(', ');
    else if (typeof value === 'number') next[key] = String(value);
    else next[key] = value ?? '';
  });
  return next;
};

const registrationSnapshot = (record) => ({
  full_name: record.full_name || '',
  phone: record.phone || '',
  email: record.email || '',
  birth_date: record.birth_date || '',
  language: record.language || '',
  program_year: record.program_year || '',
  sacrament_type: record.sacrament_type || '',
  form_type: record.form_type || '',
  donation_made: Boolean(record.donation_made),
  address_line_1: record.address_line_1 || '',
  city: record.city || '',
  state: record.state || '',
  zip: record.zip || '',
  form_data: toDraftFormData(record.form_data),
});

const restoreFormData = (draftForm, originalForm) => {
  const out = {};
  Object.entries(draftForm || {}).forEach(([key, value]) => {
    const original = originalForm?.[key];
    if (Array.isArray(original)) {
      out[key] = String(value ?? '').split(',').map((part) => part.trim()).filter(Boolean);
    } else if (typeof original === 'number') {
      out[key] = value === '' || value === null ? null : Number(value);
    } else if (typeof original === 'boolean') {
      out[key] = value === true;
    } else if (original == null) {
      out[key] = value === '' || value == null ? null : value;
    } else {
      out[key] = value;
    }
  });
  return out;
};

const RegistrationField = ({ editing, label, display, value, onChange, type = 'text', options }) => {
  if (!editing) return <Field label={label} value={display} />;
  return (
    <Grid item xs={12} sm={6} md={4}>
      <TextField
        fullWidth
        size="small"
        label={label}
        type={options ? 'text' : type}
        select={Boolean(options)}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      >
        {options?.map((option) => (
          <MenuItem key={String(option.value)} value={option.value}>{option.label}</MenuItem>
        ))}
      </TextField>
    </Grid>
  );
};

const FormDataField = ({ editing, label, value, onChange, yesLabel = 'Yes', noLabel = 'No' }) => {
  if (!editing || (value && typeof value === 'object' && !Array.isArray(value))) {
    const display = Array.isArray(value)
      ? value.map(formatValue).join(', ')
      : formatValue(value);
    return <Field label={label} value={display} />;
  }
  if (typeof value === 'boolean') {
    return (
      <RegistrationField
        editing
        label={label}
        value={value ? '1' : '0'}
        onChange={(next) => onChange(next === '1')}
        options={[{ value: '1', label: yesLabel }, { value: '0', label: noLabel }]}
      />
    );
  }
  const isDate = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  return (
    <RegistrationField
      editing
      label={label}
      value={value ?? ''}
      type={isDate ? 'date' : 'text'}
      onChange={onChange}
    />
  );
};

const getDocumentPath = (doc) => {
  if (!doc) return '';
  if (typeof doc === 'string') return doc;
  return doc.path || '';
};

const getDocumentName = (doc, fallback = 'document') => {
  if (!doc) return fallback;
  if (typeof doc === 'string') return doc.split('/').pop() || fallback;
  if (doc.name) return doc.name;
  return (doc.path || '').split('/').pop() || fallback;
};

export default function SacramentRegView() {
  const { hashid }          = useParams();
  const { t, i18n }         = useTranslation();
  const navigate            = useNavigate();
  const location            = useLocation();
  const [sendNotification]  = useNotification();
  const [record, setRecord]         = useState(null);
  const [tab, setTab]               = useState(0);
  const [officeData, setOfficeData] = useState({});
  const [status, setStatus]         = useState('pending');
  const [isSaving, setIsSaving]     = useState(false);
  const [isSavingComments, setIsSavingComments] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [openingDocIndex, setOpeningDocIndex] = useState(null);
  const [deletingDocIndex, setDeletingDocIndex] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null, name: '' });
  const [docPreviewUrls, setDocPreviewUrls] = useState({});
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    index: null,
    url: '',
    name: '',
    isImage: false,
    isPdf: false,
  });
  const [payment, setPayment]               = useState(null);
  const [isLoadingPayment, setIsLoadingPayment] = useState(true);
  const [isGeneratingPayment, setIsGeneratingPayment] = useState(false);
  const [siblings, setSiblings]             = useState([]);
  const [isLoadingSiblings, setIsLoadingSiblings] = useState(false);
  const [paymentConfirmOpen, setPaymentConfirmOpen] = useState(false);
  const [paymentDialogMode, setPaymentDialogMode] = useState('generate');
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [refundConfirmOpen, setRefundConfirmOpen] = useState(false);
  const [isRefunding, setIsRefunding] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [deductStripeFee, setDeductStripeFee] = useState(true);
  const [editingRegistration, setEditingRegistration] = useState(false);
  const [registrationDraft, setRegistrationDraft] = useState(null);
  const [registrationBaseline, setRegistrationBaseline] = useState(null);
  const [isSavingRegistration, setIsSavingRegistration] = useState(false);
  const [discardEditOpen, setDiscardEditOpen] = useState(false);
  const registrationDirty = Boolean(
    editingRegistration
    && registrationDraft
    && registrationBaseline
    && JSON.stringify(registrationDraft) !== JSON.stringify(registrationBaseline)
  );
  const navigationGuard = useUnsavedNavigationGuard(registrationDirty);
  const actionSlotRef = useRef(null);
  const actionBarRef = useRef(null);

  useLayoutEffect(() => {
    if (!editingRegistration) return undefined;
    const slot = actionSlotRef.current;
    const bar = actionBarRef.current;
    if (!slot || !bar) return undefined;

    const anchorTop = slot.getBoundingClientRect().top;
    let pinned = false;

    const clearPin = () => {
      pinned = false;
      slot.style.height = '';
      bar.style.position = '';
      bar.style.top = '';
      bar.style.right = '';
      bar.style.left = '';
      bar.style.width = '';
      bar.style.zIndex = '';
      bar.style.background = '';
      bar.style.borderRadius = '';
      bar.style.padding = '';
    };

    const update = () => {
      const slotRect = slot.getBoundingClientRect();
      const shouldPin = slotRect.top < anchorTop - 0.5;
      if (!shouldPin) {
        if (pinned) clearPin();
        return;
      }
      if (!pinned) {
        slot.style.height = `${bar.offsetHeight}px`;
        pinned = true;
      }
      bar.style.position = 'fixed';
      bar.style.top = `${anchorTop}px`;
      bar.style.left = 'auto';
      bar.style.right = `${Math.max(0, window.innerWidth - slotRect.right)}px`;
      bar.style.width = 'auto';
      bar.style.zIndex = '5';
      bar.style.background = '#fff';
      bar.style.borderRadius = '8px';
      bar.style.padding = '4px';
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      clearPin();
    };
  }, [editingRegistration]);

  useEffect(() => {
    setTab(0);
    setSiblings([]);
    setUploadFiles([]);
    setDocPreviewUrls({});
    setEditingRegistration(false);
    setRegistrationDraft(null);
    setRegistrationBaseline(null);
    setDiscardEditOpen(false);
    tracker.get(`sacrament-registrations/${hashid}`).then((res) => {
      const r = res.data.data;
      setRecord(r);
      setOfficeData(r.office_data || {});
      setStatus(r.status);
    });
    fetchPayment();
  }, [hashid]);

  // Load signed preview URLs for attached documents
  useEffect(() => {
    const docs = record?.documents || [];
    if (!hashid || docs.length === 0) {
      setDocPreviewUrls({});
      return undefined;
    }

    let cancelled = false;
    const loadPreviews = async () => {
      setLoadingPreviews(true);
      const next = {};
      await Promise.all(
        docs.map(async (_, index) => {
          try {
            const res = await tracker.get(`sacrament-registrations/${hashid}/documents/${index}`);
            const url = res.data?.data?.url;
            if (url) next[index] = url;
          } catch (e) {
            // leave missing; download will surface the error
          }
        })
      );
      if (!cancelled) {
        setDocPreviewUrls(next);
        setLoadingPreviews(false);
      }
    };
    loadPreviews();
    return () => {
      cancelled = true;
    };
  }, [hashid, record?.documents]);

  // Revoke local object URLs for pending upload previews
  useEffect(() => {
    return () => {
      uploadFiles.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, [uploadFiles]);

  useEffect(() => {
    if (!paymentConfirmOpen || !record) {
      return undefined;
    }

    if (paymentDialogMode === 'resend' && payment?.status === 'pending') {
      setIsLoadingQuote(false);
      setQuoteError(null);
      setPaymentQuote({
        lines: payment.items || [],
        total: payment.amount_total,
        currency: 'USD',
      });
      return undefined;
    }

    let cancelled = false;
    const loadQuote = async () => {
      setIsLoadingQuote(true);
      setQuoteError(null);
      setPaymentQuote(null);
      try {
        let hashids = [hashid];
        if (record.sibling_group_id) {
          const groupMembers = siblings.length > 0
            ? siblings
            : (
              await tracker.get(
                `sacrament-registrations?${new URLSearchParams({
                  sibling_group_id: record.sibling_group_id,
                  page_size: 50,
                  sort: 'created_at:desc',
                }).toString()}`
              )
            ).data.data || [];
          if (groupMembers.length > 0) {
            hashids = groupMembers.map((r) => r.hashid);
          }
        }
        const quote = await fetchSacramentPaymentQuote(tracker, hashids);
        if (!cancelled) setPaymentQuote(quote);
      } catch (error) {
        if (!cancelled) {
          setPaymentQuote(null);
          setQuoteError(
            error?.response?.data?.message || t('error_loading_payment_amount') || 'No se pudo cargar la cantidad a pagar'
          );
        }
      } finally {
        if (!cancelled) setIsLoadingQuote(false);
      }
    };

    loadQuote();
    return () => {
      cancelled = true;
    };
  }, [paymentConfirmOpen, paymentDialogMode, payment, record, siblings, hashid, t]);

  useEffect(() => {
    let cancelled = false;

    const loadSiblings = async () => {
      if (!record?.sibling_group_id) {
        setSiblings([]);
        return;
      }
      setIsLoadingSiblings(true);
      try {
        const params = new URLSearchParams({
          sibling_group_id: record.sibling_group_id,
          page_size: 50,
          sort: 'created_at:asc',
        });
        const res = await tracker.get(`sacrament-registrations?${params.toString()}`);
        if (!cancelled) {
          setSiblings(res.data.data || []);
        }
      } catch {
        if (!cancelled) setSiblings([]);
      } finally {
        if (!cancelled) setIsLoadingSiblings(false);
      }
    };

    loadSiblings();
    return () => {
      cancelled = true;
    };
  }, [record?.sibling_group_id, hashid]);

  useEffect(() => {
    if (!record || payment || isLoadingPayment) {
      return undefined;
    }
    if (record.sibling_group_id && isLoadingSiblings) {
      return undefined;
    }

    let cancelled = false;
    const loadQuote = async () => {
      try {
        let hashids = [hashid];
        if (record.sibling_group_id) {
          const groupMembers = siblings.length > 0 ? siblings : [record];
          hashids = groupMembers.map((r) => r.hashid);
        }
        const quote = await fetchSacramentPaymentQuote(tracker, hashids);
        if (!cancelled) setPaymentQuote(quote);
      } catch {
        if (!cancelled) setPaymentQuote(null);
      }
    };

    loadQuote();
    return () => {
      cancelled = true;
    };
  }, [record, payment, isLoadingPayment, isLoadingSiblings, siblings, hashid]);

  const fetchPayment = async () => {
    setIsLoadingPayment(true);
    try {
      const res = await tracker.get(`sacrament-registrations/${hashid}/payment`);
      // API returns bare null, or { data: null }, when no payment exists.
      // `null ?? res.data` would incorrectly keep `{ data: null }` as truthy.
      const payload = res.data?.data !== undefined ? res.data.data : res.data;
      setPayment(payload?.token ? payload : null);
    } catch {
      setPayment(null);
    } finally {
      setIsLoadingPayment(false);
    }
  };

  const handleGeneratePayment = async () => {
    setIsGeneratingPayment(true);
    try {
      let hashids = [hashid];
      if (record?.sibling_group_id) {
        const groupMembers = siblings.length > 0
          ? siblings
          : (
            await tracker.get(
              `sacrament-registrations?${new URLSearchParams({
                sibling_group_id: record.sibling_group_id,
                page_size: 50,
                sort: 'created_at:desc',
              }).toString()}`
            )
          ).data.data || [];
        if (groupMembers.length > 0) {
          hashids = groupMembers.map((r) => r.hashid);
        }
      }
      const res = await tracker.post('sacrament-payments', { registration_hashids: hashids });
      setPayment(res.data.data);
      setPaymentConfirmOpen(false);
      sendNotification({ msg: t('payment_link_generated'), variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_generating_payment_link'),
        variant: 'error',
      });
    } finally {
      setIsGeneratingPayment(false);
    }
  };

  const openPaymentDialog = (mode) => {
    setPaymentDialogMode(mode);
    setPaymentConfirmOpen(true);
  };

  const handleResendPayment = async () => {
    if (!payment?.token) return;
    setIsGeneratingPayment(true);
    try {
      const res = await tracker.post(`sacrament-payments/${payment.token}/resend`);
      setPayment(res.data.data);
      setPaymentConfirmOpen(false);
      sendNotification({ msg: t('payment_link_resent') || t('payment_link_generated'), variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_resending_payment_link') || t('error_generating_payment_link'),
        variant: 'error',
      });
    } finally {
      setIsGeneratingPayment(false);
    }
  };

  const handleCopyPaymentLink = () => {
    if (payment?.payment_url) {
      navigator.clipboard.writeText(payment.payment_url);
      sendNotification({ msg: t('copied_to_clipboard') || 'Enlace copiado', variant: 'success' });
    }
  };

  const estimateStripeFee = (gross, paymentData = payment) => {
    const amount = Number(gross || 0);
    if (!(amount > 0)) return 0;
    const pct = Number(paymentData?.stripe_fee_percent ?? 2.9);
    const fixed = Number(paymentData?.stripe_fee_fixed ?? 0.3);
    return Math.round((amount * (pct / 100) + fixed) * 100) / 100;
  };

  const getRefundBreakdown = (paymentData = payment) => {
    const total = Number(paymentData?.amount_total || 0);
    const refunded = Number(paymentData?.amount_refunded || 0);
    const refundable = Number(
      paymentData?.refundable_amount ?? Math.max(0, total - refunded)
    );
    const fee = Number(
      paymentData?.estimated_stripe_fee > 0
        ? paymentData.estimated_stripe_fee
        : estimateStripeFee(total, paymentData)
    );
    const net = Number(
      paymentData?.net_refundable_amount > 0
        ? paymentData.net_refundable_amount
        : Math.max(0, Math.round((total - fee - refunded) * 100) / 100)
    );
    return { total, refunded, refundable, fee, net };
  };

  const openRefundDialog = async () => {
    setDeductStripeFee(true);
    setRefundReason('');
    setRefundConfirmOpen(true);

    let paymentData = payment;
    try {
      const res = await tracker.get(`sacrament-registrations/${hashid}/payment`);
      const payload = res.data?.data !== undefined ? res.data.data : res.data;
      if (payload?.token) {
        paymentData = payload;
        setPayment(payload);
      }
    } catch {
      // Keep existing payment state if refresh fails.
    }

    const { refundable, net } = getRefundBreakdown(paymentData);
    const suggested = net > 0 ? net : refundable;
    setRefundAmount(suggested > 0 ? suggested.toFixed(2) : '');
  };

  const handleDeductFeeToggle = (checked) => {
    setDeductStripeFee(checked);
    const { refundable, net } = getRefundBreakdown();
    let suggested = refundable;
    if (checked && net > 0) {
      suggested = net;
    }
    setRefundAmount(suggested > 0 ? suggested.toFixed(2) : '');
  };

  const handleRefundPayment = async () => {
    if (!payment?.token) return;
    setIsRefunding(true);
    try {
      const payload = { deduct_stripe_fee: deductStripeFee };
      const amount = Number(refundAmount);
      if (Number.isFinite(amount) && amount > 0) {
        payload.amount = amount;
      }
      if (refundReason.trim()) {
        payload.reason = refundReason.trim();
      }
      const res = await tracker.post(`sacrament-payments/${payment.token}/refund`, payload);
      setPayment(res.data.data);
      setRefundConfirmOpen(false);
      sendNotification({ msg: t('payment_refunded'), variant: 'success' });
      // Refresh registration so office payment_status reflects refund.
      const refreshed = await tracker.get(`sacrament-registrations/${hashid}`);
      const r = refreshed.data.data;
      setRecord(r);
      setOfficeData(r.office_data || {});
      setStatus(r.status);
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_refunding_payment'),
        variant: 'error',
      });
    } finally {
      setIsRefunding(false);
    }
  };

  const PAYMENT_STATUS_COLORS = {
    pending: 'warning',
    paid: 'success',
    expired: 'default',
    void: 'default',
    refunded: 'error',
    partially_refunded: 'warning',
  };

  const handleOfficeChange = (field, value) => {
    setOfficeData((prev) => ({ ...prev, [field]: value }));
  };

  const applyOfficeData = (nextOfficeData) => {
    setOfficeData(nextOfficeData || {});
    setRecord((current) => (current ? { ...current, office_data: nextOfficeData || {} } : current));
  };

  const handleSaveOffice = async () => {
    setIsSaving(true);
    try {
      const res = await tracker.post(`sacrament-registrations/${hashid}/update`, { office_data: officeData, status });
      applyOfficeData(res.data?.data?.office_data || officeData);
      if (res.data?.data?.status) setStatus(res.data.data.status);
      sendNotification({ msg: t('saved_successfully'), variant: 'success' });
    } catch {
      sendNotification({ msg: t('error_saving'), variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveOfficeComments = async (nextComments) => {
    const nextOfficeData = { ...officeData, office_comments: nextComments };
    setIsSavingComments(true);
    try {
      const res = await tracker.post(`sacrament-registrations/${hashid}/update`, { office_data: nextOfficeData });
      applyOfficeData(res.data?.data?.office_data || nextOfficeData);
      sendNotification({ msg: t('saved_successfully'), variant: 'success' });
    } catch (error) {
      sendNotification({ msg: t('error_saving'), variant: 'error' });
      throw error;
    } finally {
      setIsSavingComments(false);
    }
  };

  const clearUploadFiles = () => {
    setUploadFiles((prev) => {
      prev.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      return [];
    });
  };

  const handleUploadFilesSelected = (fileList) => {
    const files = Array.from(fileList || []);
    setUploadFiles((prev) => {
      prev.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
      return files.map((file) => ({
        file,
        previewUrl: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
      }));
    });
  };

  const handleUpload = async () => {
    if (!uploadFiles.length) return;
    setIsUploading(true);
    try {
      const fd = new FormData();
      uploadFiles.forEach((item, index) => {
        fd.append(`documents[${index}]`, item.file);
      });
      // Do not set Content-Type manually — axios must add the multipart boundary.
      const res = await tracker.post(`sacrament-registrations/${hashid}/upload`, fd);
      setRecord(res.data.data);
      clearUploadFiles();
      sendNotification({ msg: t('document_uploaded'), variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('document_not_found'),
        variant: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const isImagePath = (path = '') => /\.(jpe?g|png|gif|webp)$/i.test(path);
  const isPdfPath = (path = '') => /\.pdf$/i.test(path);

  const resolveDocumentUrl = async (index) => {
    const cached = docPreviewUrls[index];
    if (cached) return cached;
    const res = await tracker.get(`sacrament-registrations/${hashid}/documents/${index}`);
    const url = res.data?.data?.url;
    if (!url) {
      throw new Error('missing url');
    }
    setDocPreviewUrls((prev) => ({ ...prev, [index]: url }));
    return url;
  };

  const handleDownloadDocument = async (index) => {
    setOpeningDocIndex(index);
    try {
      const fileName = getDocumentName(record?.documents?.[index], `document-${index + 1}`);
      // Download through the authenticated API (avoids CORS on cross-origin storage URLs).
      const response = await tracker.get(
        `sacrament-registrations/${hashid}/documents/${index}/download`,
        { responseType: 'blob' },
      );
      const blob = response.data;
      if (blob?.type?.includes('application/json')) {
        const text = await blob.text();
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || 'download failed');
      }
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      let msg = t('document_not_found');
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          msg = parsed?.message || msg;
        } catch (parseError) {
          // keep default message
        }
      } else if (data?.message || error?.message) {
        msg = data?.message || error.message;
      }
      sendNotification({ msg, variant: 'error' });
    } finally {
      setOpeningDocIndex(null);
    }
  };

  const handlePreviewDocument = async (index) => {
    setOpeningDocIndex(index);
    try {
      const doc = record?.documents?.[index];
      const docPath = getDocumentPath(doc);
      const url = await resolveDocumentUrl(index);
      setPreviewDialog({
        open: true,
        index,
        url,
        name: getDocumentName(doc, t('preview')),
        isImage: isImagePath(docPath) || isImagePath(getDocumentName(doc)),
        isPdf: isPdfPath(docPath) || isPdfPath(getDocumentName(doc)),
      });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('document_not_found'),
        variant: 'error',
      });
    } finally {
      setOpeningDocIndex(null);
    }
  };

  const openDeleteDocumentConfirm = (index) => {
    const fileName = getDocumentName(record?.documents?.[index], t('document'));
    setDeleteConfirm({ open: true, index, name: fileName });
  };

  const handleDeleteDocument = async () => {
    const index = deleteConfirm.index;
    if (index == null) return;

    setDeletingDocIndex(index);
    try {
      const res = await tracker.delete(`sacrament-registrations/${hashid}/documents/${index}`);
      setRecord(res.data.data);
      setDocPreviewUrls({});
      setDeleteConfirm({ open: false, index: null, name: '' });
      sendNotification({ msg: t('document_deleted'), variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('document_not_found'),
        variant: 'error',
      });
    } finally {
      setDeletingDocIndex(null);
    }
  };

  const handleBack = () => {
    const search = location.state?.listSearch;
    if (typeof search === 'string') {
      navigate({ pathname: '/dashboard/sacrament-reg', search });
      return;
    }
    navigate('/dashboard/sacrament-reg');
  };

  if (!record) {
    return (
      <Page title={t('sacrament_regs')}>
        <Container maxWidth="xl">
          <BackButton onClick={handleBack} sx={{ mb: 2 }} />
          <div style={{ padding: 40 }}>Loading...</div>
        </Container>
      </Page>
    );
  }

  const viewed = editingRegistration && registrationDraft
    ? { ...record, ...registrationDraft }
    : record;
  const formData = viewed.form_data || {};

  const yesNoOptions = [
    { value: '1', label: t('yes') },
    { value: '0', label: t('no') },
  ];
  const languageOptions = withCurrentOption([
    { value: 'es', label: 'Español' },
    { value: 'en', label: 'English' },
  ], viewed.language);
  const sacramentOptions = withCurrentOption([
    { value: 'baptism', label: t('_tab_baptism') },
    { value: 'communion', label: t('_tab_communion') },
    { value: 'confirmation', label: t('_tab_confirmation') },
    { value: 'matrimony', label: t('_tab_matrimony') },
  ], viewed.sacrament_type);
  const formTypeOptions = withCurrentOption([
    { value: 'child', label: t('child') },
    { value: 'adult', label: t('adult') },
  ], viewed.form_type);

  const setColumn = (key, value) => {
    setRegistrationDraft((prev) => ({ ...prev, [key]: value }));
  };
  const setFormField = (key, value) => {
    setRegistrationDraft((prev) => ({
      ...prev,
      form_data: { ...(prev?.form_data || {}), [key]: value },
    }));
  };

  const stopEditingRegistration = () => {
    setEditingRegistration(false);
    setRegistrationDraft(null);
    setRegistrationBaseline(null);
  };

  const startEditingRegistration = () => {
    const snapshot = registrationSnapshot(record);
    setRegistrationBaseline(snapshot);
    setRegistrationDraft(snapshot);
    setEditingRegistration(true);
    setTab(0);
  };

  const requestStopEditing = () => {
    if (!registrationDirty) {
      stopEditingRegistration();
      return;
    }
    setDiscardEditOpen(true);
  };

  const handleSaveRegistration = async () => {
    if (!registrationDraft) return;
    setIsSavingRegistration(true);
    try {
      const res = await tracker.post(`sacrament-registrations/${hashid}/update`, {
        full_name: registrationDraft.full_name,
        phone: registrationDraft.phone,
        email: registrationDraft.email || null,
        birth_date: registrationDraft.birth_date || null,
        language: registrationDraft.language,
        program_year: registrationDraft.program_year,
        sacrament_type: registrationDraft.sacrament_type,
        form_type: registrationDraft.form_type,
        donation_made: Boolean(registrationDraft.donation_made),
        address_line_1: registrationDraft.address_line_1,
        city: registrationDraft.city,
        state: registrationDraft.state,
        zip: registrationDraft.zip,
        form_data: restoreFormData(registrationDraft.form_data, record.form_data),
      });
      const saved = res.data.data;
      setRecord(saved);
      setOfficeData(saved.office_data || {});
      setStatus(saved.status);
      stopEditingRegistration();
      sendNotification({ msg: t('saved_successfully'), variant: 'success' });
    } catch (error) {
      sendNotification({
        msg: error?.response?.data?.message || t('error_saving'),
        variant: 'error',
      });
    } finally {
      setIsSavingRegistration(false);
    }
  };

  const stayOnPage = () => {
    navigationGuard.stay();
    setDiscardEditOpen(false);
  };

  const confirmLeave = () => {
    if (discardEditOpen) {
      setDiscardEditOpen(false);
      stopEditingRegistration();
      return;
    }
    navigationGuard.confirm();
  };
  const documents = record.documents || [];

  const SACRAMENT_COLORS = {
    baptism: 'primary', communion: 'success', confirmation: 'warning', matrimony: 'secondary',
  };

  const CHILD_SACRAMENTS = ['Baptism', 'Pre-Communion', '1st Communion / Eucharist', 'Bible', 'Pre-Confirmation', 'Confirmation'];
  const ADULT_SACRAMENTS = ['Baptism', 'Pre-Communion', '1st Communion / Eucharist', 'Bible', 'Pre-Confirmation', 'Confirmation', 'Marriage'];
  const sacOptions  = record.form_type === 'adult' ? ADULT_SACRAMENTS : CHILD_SACRAMENTS;
  const DOCS_ON_FILE = ['Birth Certificate', 'Baptism Certificate', '1st Communion', 'Confirmation'];
  const isResendDialog = paymentDialogMode === 'resend';
  let paymentDialogConfirmKey = 'generate_payment_link_confirm';
  if (isResendDialog && record.sibling_group_id) {
    paymentDialogConfirmKey = 'resend_payment_link_confirm_siblings';
  } else if (isResendDialog) {
    paymentDialogConfirmKey = 'resend_payment_link_confirm';
  } else if (record.sibling_group_id) {
    paymentDialogConfirmKey = 'generate_payment_link_confirm_siblings';
  }

  return (
    <>
      <SacramentPrintTemplate record={record} />
      <Page title={`${t('sacrament_regs')} — ${record.full_name}`}>
      <Container maxWidth="xl">
        <Card>
          {/* Header inside card — matches back-office standard */}
          <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
            <BackButton onClick={handleBack} />
            <Typography variant="h5" fontWeight={700} sx={{ flexGrow: 1, color: '#032C65' }}>{record.full_name}</Typography>
            <Chip label={toTitleCase(record.sacrament_type)} color={SACRAMENT_COLORS[record.sacrament_type] || 'default'} />
            <Chip label={toTitleCase(record.form_type)} variant="outlined" />
            <Chip label={record.program_year} variant="outlined" />
            <Chip label={toTitleCase(record.status)} color={record.status === 'pending' ? 'warning' : 'success'} />
            {record.requires_religious_education && (
              <Chip
                label={t('religious_education')}
                size="small"
                sx={{ background: '#fce7f3', color: '#9d174d', fontWeight: 600 }}
              />
            )}
            {record.sibling_group_id && (
              <Chip
                label={record.sibling_group_id}
                color="info"
                variant="outlined"
                title={t('sibling_group') || 'Grupo de hermanos'}
              />
            )}
            <PrintButton />
          </CardContent>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab label={t('registration_data')} />
            <Tab label={`🔒 ${t('office_only')}`} />
            <Tab label={t('documents')} />
            <Tab label={`💬 ${t('reply')}`} />
            {record.sibling_group_id && (
              <Tab
                label={`👨‍👩‍👧‍👦 ${t('siblings') || 'Hermanos'}${
                  siblings.length > 0 ? ` (${siblings.length})` : ''
                }`}
              />
            )}
            <Tab label={`💳 ${t('payment') || 'Pago'}`} />
          </Tabs>

          {/* ── Tab 0: Form Data ── */}
          <TabPanel value={tab} index={0}>
            <CardContent>
              <Box ref={actionSlotRef} sx={{ mb: 2 }}>
              <Box
                ref={actionBarRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                {editingRegistration ? (
                  <>
                    <Button variant="outlined" onClick={requestStopEditing}>
                      {t('cancel_editing')}
                    </Button>
                    <LoadingButton
                      variant="contained"
                      loading={isSavingRegistration}
                      disabled={!registrationDirty}
                      startIcon={<SaveIcon />}
                      onClick={handleSaveRegistration}
                      sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                    >
                      {t('save_changes')}
                    </LoadingButton>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={startEditingRegistration}
                    sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                  >
                    {t('edit')}
                  </Button>
                )}
              </Box>
              </Box>
              <Grid container spacing={2}>
                <RegistrationField editing={editingRegistration} label={t('name')} display={viewed.full_name} value={viewed.full_name} onChange={(value) => setColumn('full_name', value)} />
                <RegistrationField editing={editingRegistration} label={t('phone')} display={viewed.phone} value={viewed.phone} onChange={(value) => setColumn('phone', value)} />
                <RegistrationField editing={editingRegistration} label={t('email')} display={viewed.email} value={viewed.email} onChange={(value) => setColumn('email', value)} />
                <RegistrationField editing={editingRegistration} label={t('birth_date')} type="date" display={viewed.birth_date ? fDateOnly(viewed.birth_date, i18n.language) : '—'} value={viewed.birth_date || ''} onChange={(value) => setColumn('birth_date', value)} />
                <Field label={t('Age')} value={viewed.birth_date ? formatAge(viewed.birth_date, i18n.language) : '—'} />
                <RegistrationField editing={editingRegistration} label={t('language')} display={formatValue(viewed.language)} value={viewed.language} options={languageOptions} onChange={(value) => setColumn('language', value)} />
                {editingRegistration ? (
                  <>
                    <RegistrationField editing label={t('address')} value={viewed.address_line_1} onChange={(value) => setColumn('address_line_1', value)} />
                    <RegistrationField editing label={t('city')} value={viewed.city} onChange={(value) => setColumn('city', value)} />
                    <RegistrationField editing label={t('state')} value={viewed.state} onChange={(value) => setColumn('state', value)} />
                    <RegistrationField editing label={t('zip')} value={viewed.zip} onChange={(value) => setColumn('zip', value)} />
                  </>
                ) : (
                  <Field label={t('full_address')} value={record.full_address} />
                )}
                <RegistrationField editing={editingRegistration} label={t('program_year')} display={viewed.program_year} value={viewed.program_year} onChange={(value) => setColumn('program_year', value)} />
                <RegistrationField editing={editingRegistration} label={t('sacrament')} display={toTitleCase(viewed.sacrament_type)} value={viewed.sacrament_type} options={sacramentOptions} onChange={(value) => setColumn('sacrament_type', value)} />
                <RegistrationField editing={editingRegistration} label={t('type')} display={toTitleCase(viewed.form_type)} value={viewed.form_type} options={formTypeOptions} onChange={(value) => setColumn('form_type', value)} />
                <RegistrationField editing={editingRegistration} label={t('donation_made')} display={viewed.donation_made ? '✅ Si' : '❌ No'} value={viewed.donation_made ? '1' : '0'} options={yesNoOptions} onChange={(value) => setColumn('donation_made', value === '1')} />
              </Grid>

              {/* ── Baptism-specific sections (only when sacrament is baptism) ── */}
              {record.sacrament_type === 'baptism' && (
                <>
                  <Divider sx={{ my: 3 }} />

                  {/* Child info */}
                  {(editingRegistration || formData.child_name || formData.birth_place) && (
                    <>
                      <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {t('section_child_info')}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <RegistrationField editing={editingRegistration} label={t('Child_Name')} display={formData.child_name} value={formData.child_name || ''} onChange={(value) => setFormField('child_name', value)} />
                        <RegistrationField editing={editingRegistration} label={t('Birth_Date')} type="date" display={formData.birth_date ? fDateOnly(formData.birth_date, i18n.language) : '—'} value={formData.birth_date || ''} onChange={(value) => setFormField('birth_date', value)} />
                        <Field label={t('Age')} value={formData.birth_date ? formatAge(formData.birth_date, i18n.language) : '—'} />
                        <RegistrationField editing={editingRegistration} label={t('Birth_Place')} display={formData.birth_place} value={formData.birth_place || ''} onChange={(value) => setFormField('birth_place', value)} />
                      </Grid>
                    </>
                  )}

                  {/* Parents */}
                  {(editingRegistration || formData.mother_name || formData.father_name) && (
                    <>
                      <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {t('section_family')}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <RegistrationField editing={editingRegistration} label={t("Mother's_Name")} display={formData.mother_name} value={formData.mother_name || ''} onChange={(value) => setFormField('mother_name', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Mother's_Phone")} display={formData.mother_phone} value={formData.mother_phone || ''} onChange={(value) => setFormField('mother_phone', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Mother's_Email")} display={formData.mother_email} value={formData.mother_email || ''} onChange={(value) => setFormField('mother_email', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Father's_Name")} display={formData.father_name} value={formData.father_name || ''} onChange={(value) => setFormField('father_name', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Father's_Phone")} display={formData.father_phone} value={formData.father_phone || ''} onChange={(value) => setFormField('father_phone', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Father's_Email")} display={formData.father_email} value={formData.father_email || ''} onChange={(value) => setFormField('father_email', value)} />
                      </Grid>
                    </>
                  )}

                  {/* Godparents */}
                  {(editingRegistration || formData.godfather_name || formData.godmother_name) && (
                    <>
                      <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {t('section_godparents') || 'Godparents'}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <RegistrationField editing={editingRegistration} label={t("Godfather's_Name")} display={formData.godfather_name} value={formData.godfather_name || ''} onChange={(value) => setFormField('godfather_name', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Godfather's_Religion")} display={formData.godfather_religion} value={formData.godfather_religion || ''} onChange={(value) => setFormField('godfather_religion', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Godfather's_Phone")} display={formData.godfather_phone} value={formData.godfather_phone || ''} onChange={(value) => setFormField('godfather_phone', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Godfather's_Email")} display={formData.godfather_email} value={formData.godfather_email || ''} onChange={(value) => setFormField('godfather_email', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Godmother's_Name")} display={formData.godmother_name} value={formData.godmother_name || ''} onChange={(value) => setFormField('godmother_name', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Godmother's_Religion")} display={formData.godmother_religion} value={formData.godmother_religion || ''} onChange={(value) => setFormField('godmother_religion', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Godmother's_Phone")} display={formData.godmother_phone} value={formData.godmother_phone || ''} onChange={(value) => setFormField('godmother_phone', value)} />
                        <RegistrationField editing={editingRegistration} label={t("Godmother's_Email")} display={formData.godmother_email} value={formData.godmother_email || ''} onChange={(value) => setFormField('godmother_email', value)} />
                      </Grid>
                    </>
                  )}

                  {/* Office Only fields specific to Baptism */}
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_office_use')}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <RegistrationField editing={editingRegistration} label={t('receipt_number')} display={formData.receipt_number} value={formData.receipt_number || ''} onChange={(value) => setFormField('receipt_number', value)} />
                    <RegistrationField editing={editingRegistration} label={t('parish_name')} display={formData.parish_name} value={formData.parish_name || ''} onChange={(value) => setFormField('parish_name', value)} />
                    <RegistrationField editing={editingRegistration} label={t('parish_language')} display={formData.parish_language} value={formData.parish_language || ''} onChange={(value) => setFormField('parish_language', value)} />
                    <RegistrationField editing={editingRegistration} label={t('baptism_date')} type="date" display={formData.baptism_date ? fDateOnly(formData.baptism_date, i18n.language) : '—'} value={formData.baptism_date || ''} onChange={(value) => setFormField('baptism_date', value)} />
                    <RegistrationField editing={editingRegistration} label={t('class_date')} type="date" display={formData.class_date ? fDateOnly(formData.class_date, i18n.language) : '—'} value={formData.class_date || ''} onChange={(value) => setFormField('class_date', value)} />
                    <RegistrationField editing={editingRegistration} label={t('payment_date')} type="date" display={formData.payment_date ? fDateOnly(formData.payment_date, i18n.language) : '—'} value={formData.payment_date || ''} onChange={(value) => setFormField('payment_date', value)} />
                    <RegistrationField editing={editingRegistration} label={t('birth_cert_verified')} display={formData.birth_cert_verified ? '✅ Si' : '❌ No'} value={formData.birth_cert_verified ? '1' : '0'} options={yesNoOptions} onChange={(value) => setFormField('birth_cert_verified', value === '1')} />
                    <RegistrationField editing={editingRegistration} label={t('permission_needed')} display={formData.permission_needed ? '✅ Si' : '❌ No'} value={formData.permission_needed ? '1' : '0'} options={yesNoOptions} onChange={(value) => setFormField('permission_needed', value === '1')} />
                  </Grid>
                </>
              )}

              {record.sacrament_type === 'confirmation' && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {t('section_confirmation_sponsor')}
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <RegistrationField editing={editingRegistration} label={t('sponsor_name')} display={formData.sponsor_name} value={formData.sponsor_name || ''} onChange={(value) => setFormField('sponsor_name', value)} />
                    <RegistrationField editing={editingRegistration} label={t('sponsor_religion')} display={formData.sponsor_religion} value={formData.sponsor_religion || ''} onChange={(value) => setFormField('sponsor_religion', value)} />
                    <RegistrationField editing={editingRegistration} label={t('sponsor_phone')} display={formData.sponsor_phone} value={formData.sponsor_phone || ''} onChange={(value) => setFormField('sponsor_phone', value)} />
                    <RegistrationField editing={editingRegistration} label={t('sponsor_email')} display={formData.sponsor_email} value={formData.sponsor_email || ''} onChange={(value) => setFormField('sponsor_email', value)} />
                  </Grid>
                </>
              )}

              {/* Other form_data fields (generic) */}
              {Object.keys(formData).filter((key) => record.sacrament_type !== 'confirmation' || !CONFIRMATION_SPONSOR_FIELDS.includes(key)).length > 0 && record.sacrament_type !== 'baptism' && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('extended_form_data')}
                  </Typography>
                  <Grid container spacing={2}>
                    {Object.entries(formData)
                      .filter(([key]) => record.sacrament_type !== 'confirmation' || !CONFIRMATION_SPONSOR_FIELDS.includes(key))
                      .map(([key, val]) => (
                      <FormDataField
                        key={key}
                        editing={editingRegistration}
                        label={toTitleCase(key)}
                        value={val}
                        onChange={(value) => setFormField(key, value)}
                        yesLabel={t('yes')}
                        noLabel={t('no')}
                      />
                    ))}
                  </Grid>
                </>
              )}
            </CardContent>
          </TabPanel>

          {/* ── Tab 1: Office Only ── */}
          <TabPanel value={tab} index={1}>
            <CardContent>
              <OfficeComments
                comments={officeData.office_comments || []}
                saving={isSavingComments}
                onSave={handleSaveOfficeComments}
              />
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label={t('re_group_class')} size="small"
                    value={officeData.group || ''}
                    onChange={(e) => handleOfficeChange('group', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth label={t('catechist')} size="small"
                    value={officeData.catechist || ''}
                    onChange={(e) => handleOfficeChange('catechist', e.target.value)}
                  />
                </Grid>

                {/* Sacraments to prepare */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('sacraments_to_prepare')}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {sacOptions.map((s) => (
                      <FormControlLabel key={s} control={
                        <Checkbox
                          checked={(officeData.sacraments_to_prepare || []).includes(s)}
                          onChange={(e) => {
                            const current = officeData.sacraments_to_prepare || [];
                            handleOfficeChange('sacraments_to_prepare', e.target.checked ? [...current, s] : current.filter((x) => x !== s));
                          }}
                        />
                      } label={s} />
                    ))}
                  </Box>
                </Grid>

                {/* Documents on file */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2">{t('documents_on_file')}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {DOCS_ON_FILE.map((d) => (
                      <FormControlLabel key={d} control={
                        <Checkbox
                          checked={(officeData.documents_on_file || []).includes(d)}
                          onChange={(e) => {
                            const current = officeData.documents_on_file || [];
                            handleOfficeChange('documents_on_file', e.target.checked ? [...current, d] : current.filter((x) => x !== d));
                          }}
                        />
                      } label={d} />
                    ))}
                  </Box>
                </Grid>

                {/* Payments */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('payments')}</Typography>
                  {[1, 2].map((n) => (
                    <Grid container spacing={1} key={n} sx={{ mb: 1 }}>
                      <Grid item xs={12} sm={2}>
                        <TextField fullWidth label={t('date')} type="date" size="small" InputLabelProps={{ shrink: true }}
                          value={officeData[`payment${n}_date`] || ''}
                          onChange={(e) => handleOfficeChange(`payment${n}_date`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <MoneyInput
                          fullWidth
                          size="small"
                          label={t('amount')}
                          value={officeData[`payment${n}_amount`] || ''}
                          commitEmptyTo=""
                          onChange={(next) => handleOfficeChange(`payment${n}_amount`, next)}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <MoneyInput
                          fullWidth
                          size="small"
                          label={t('balance')}
                          value={officeData[`payment${n}_balance`] || ''}
                          commitEmptyTo=""
                          onChange={(next) => handleOfficeChange(`payment${n}_balance`, next)}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField fullWidth label={t('receipt_no')} size="small"
                          value={officeData[`payment${n}_receipt`] || ''}
                          onChange={(e) => handleOfficeChange(`payment${n}_receipt`, e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <TextField fullWidth label={t('initial')} size="small"
                          value={officeData[`payment${n}_initial`] || ''}
                          onChange={(e) => handleOfficeChange(`payment${n}_initial`, e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  ))}

                  {record.form_type === 'adult' && (
                    <FormControlLabel control={
                      <Checkbox
                        checked={!!officeData.late_fee}
                        onChange={(e) => handleOfficeChange('late_fee', e.target.checked)}
                      />
                    } label={`${t('print_label_late_fee')} $20`} />
                  )}
                </Grid>

                {/* Payment status + overall status */}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('payment_status')}</InputLabel>
                    <Select
                      value={officeData.payment_status || 'pending'}
                      label={t('payment_status')}
                      onChange={(e) => handleOfficeChange('payment_status', e.target.value)}
                    >
                      <MenuItem value="pending">{t('pending')}</MenuItem>
                      <MenuItem value="partial">Parcial</MenuItem>
                      <MenuItem value="paid">Pagado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('status')}</InputLabel>
                    <Select value={status} label={t('status')} onChange={(e) => setStatus(e.target.value)}>
                      <MenuItem value="pending">{t('pending')}</MenuItem>
                      <MenuItem value="active">Activo</MenuItem>
                      <MenuItem value="completed">Completado</MenuItem>
                      <MenuItem value="rejected">{t('rejected')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Button variant="contained" onClick={handleSaveOffice} disabled={isSaving}
                    sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}>
                    {isSaving ? 'Guardando...' : t('save_office_changes')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </TabPanel>

          {/* ── Tab 2: Documents ── */}
          <TabPanel value={tab} index={2}>
            <CardContent>
              {documents.length === 0 ? (
                <Typography color="text.secondary">{t('no_documents')}</Typography>
              ) : (
                <Box sx={{ mb: 3, display: 'grid', gap: 2 }}>
                  {loadingPreviews && (
                    <Typography variant="caption" color="text.secondary">{t('loading') || 'Loading previews...'}</Typography>
                  )}
                  {documents.map((doc, i) => {
                    const previewUrl = docPreviewUrls[i];
                    const docPath = getDocumentPath(doc);
                    const fileName = getDocumentName(doc);
                    const imageLike = isImagePath(docPath) || isImagePath(fileName);
                    const pdfLike = isPdfPath(docPath) || isPdfPath(fileName);
                    return (
                      <Box
                        key={`${docPath}-${fileName}-${i}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 1.5,
                          border: '1px solid #e0e3e8',
                          borderRadius: 1,
                          background: '#fafbfc',
                        }}
                      >
                        <Box
                          sx={{
                            width: 88,
                            height: 88,
                            flexShrink: 0,
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '1px solid #dde1e6',
                            background: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {previewUrl && imageLike ? (
                            <Box
                              component="img"
                              src={previewUrl}
                              alt={fileName}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                              onClick={() => handlePreviewDocument(i)}
                            />
                          ) : (
                            <Typography variant="caption" sx={{ px: 1, textAlign: 'center' }}>
                              {pdfLike ? 'PDF' : '📄'}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap title={fileName}>{fileName}</Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleDownloadDocument(i)}
                              disabled={openingDocIndex === i || deletingDocIndex === i}
                            >
                              {openingDocIndex === i ? '...' : t('download')}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handlePreviewDocument(i)}
                              disabled={openingDocIndex === i || deletingDocIndex === i}
                            >
                              {t('preview')}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => openDeleteDocumentConfirm(i)}
                              disabled={deletingDocIndex === i}
                            >
                              {deletingDocIndex === i ? '...' : t('remove')}
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('upload_document')}</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  multiple
                  onChange={(e) => handleUploadFilesSelected(e.target.files)}
                />
                <Button variant="outlined" onClick={handleUpload} disabled={!uploadFiles.length || isUploading}>
                  {isUploading ? 'Subiendo...' : t('upload')}
                </Button>
                {!!uploadFiles.length && (
                  <Button size="small" color="inherit" onClick={clearUploadFiles} disabled={isUploading}>
                    {t('clear_selection')}
                  </Button>
                )}
              </Box>
              {!!uploadFiles.length && (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1.5 }}>
                  {uploadFiles.map((item, i) => (
                    <Box
                      key={`${item.file.name}-${i}`}
                      sx={{
                        border: '1px solid #e0e3e8',
                        borderRadius: 1,
                        overflow: 'hidden',
                        background: '#fff',
                      }}
                    >
                      <Box
                        sx={{
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: '#f5f7fa',
                        }}
                      >
                        {item.previewUrl ? (
                          <Box component="img" src={item.previewUrl} alt={item.file.name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <Typography variant="caption">PDF</Typography>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', p: 1 }} noWrap title={item.file.name}>
                        {item.file.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </TabPanel>
          {/* ── Tab 3: Reply ── */}
          <TabPanel value={tab} index={3}>
            <CardContent>
              {formData.comments && (
                <>
                  <Typography variant="caption" color="text.secondary">{t('comments')}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 3, p: 2, background: '#f7f8fa', borderRadius: 1, border: '1px solid #e0e3e8' }}>
                    {formData.comments}
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </>
              )}
              <CommentReplyBox
                hashid={record.hashid}
                endpoint="sacrament-registrations"
                existingReply={record.comment_reply}
                recipientEmail={record.email || formData.mother_email || formData.father_email || ''}
              />
            </CardContent>
          </TabPanel>

          {/* ── Tab 4: Siblings (only when family group exists) ── */}
          {record.sibling_group_id && (
            <TabPanel value={tab} index={4}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('sibling_group') || 'Grupo de hermanos'}:{' '}
                  <Chip label={record.sibling_group_id} size="small" color="info" variant="outlined" />
                </Typography>

                {isLoadingSiblings && (
                  <Typography color="text.secondary">{t('loading') || 'Cargando...'}</Typography>
                )}

                {!isLoadingSiblings && siblings.length === 0 && (
                  <Typography color="text.secondary">
                    {t('no_siblings_found') || 'No se encontraron otros registros en este grupo familiar.'}
                  </Typography>
                )}

                {!isLoadingSiblings && siblings.length > 0 && (
                  <List sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    {siblings.map((sibling, idx) => {
                      const isCurrent = sibling.hashid === record.hashid;
                      return (
                        <ListItem
                          key={sibling.hashid}
                          disablePadding
                          divider={idx < siblings.length - 1}
                          secondaryAction={
                            isCurrent ? (
                              <Chip size="small" label={t('current') || 'Actual'} color="default" />
                            ) : (
                              <IconButton
                                edge="end"
                                component={RouterLink}
                                to={`/dashboard/sacrament-reg/view/${sibling.hashid}`}
                                state={location.state}
                                size="small"
                                color="primary"
                                title={t('open_registration') || 'Abrir registro'}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            )
                          }
                        >
                          <ListItemButton
                            component={isCurrent ? 'div' : RouterLink}
                            to={isCurrent ? undefined : `/dashboard/sacrament-reg/view/${sibling.hashid}`}
                            state={isCurrent ? undefined : location.state}
                            disabled={isCurrent}
                            sx={{ py: 1.5, pr: 7 }}
                          >
                            <ListItemText
                              primary={sibling.full_name}
                              secondary={[
                                toTitleCase(sibling.sacrament_type),
                                toTitleCase(sibling.form_type),
                                sibling.birth_date
                                  ? fDateOnly(sibling.birth_date, i18n.language)
                                  : null,
                                toTitleCase(sibling.status),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                              primaryTypographyProps={{ fontWeight: isCurrent ? 700 : 500 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </CardContent>
            </TabPanel>
          )}

          {/* ── Tab: Payment ── */}
          <TabPanel value={tab} index={record.sibling_group_id ? 5 : 4}>
            <CardContent>
              {isLoadingPayment && <Typography color="text.secondary">{t('loading') || 'Cargando...'}</Typography>}

              {!isLoadingPayment && !payment && (
                <Box>
                  {paymentQuote && Number(paymentQuote.total) <= 0 ? (
                    <Alert severity="info">{t('no_sacrament_payment_required')}</Alert>
                  ) : (
                    <>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {t('no_payment_link_yet')}
                      </Typography>
                      {record.sibling_group_id && (
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                          {t('payment_includes_siblings_hint')}
                        </Typography>
                      )}
                      <LoadingButton
                        variant="contained"
                        loading={isGeneratingPayment}
                        onClick={() => openPaymentDialog('generate')}
                        sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                      >
                        {t('generate_payment_link')}
                      </LoadingButton>
                    </>
                  )}
                </Box>
              )}

              {!isLoadingPayment && payment && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Chip
                      label={toTitleCase(payment.status)}
                      color={PAYMENT_STATUS_COLORS[payment.status] || 'default'}
                    />
                    <Typography variant="h6">
                      {t('total_amount') || 'Monto total'}: ${Number(payment.amount_total).toFixed(2)}
                    </Typography>
                    {Number(payment.amount_refunded) > 0 && (
                      <Typography variant="body1" color="error.main">
                        {t('amount_refunded')}: ${Number(payment.amount_refunded).toFixed(2)}
                      </Typography>
                    )}
                  </Box>

                  {(payment.last_sent_at || payment.created_at) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('last_payment_link_sent')}: {fDateTime(payment.last_sent_at || payment.created_at)}
                      {payment.last_sent_by_name ? ` · ${payment.last_sent_by_name}` : ''}
                    </Typography>
                  )}

                  {payment.status === 'pending' && payment.payment_url && (
                    <TextField
                      fullWidth
                      size="small"
                      label={t('payment_link')}
                      value={payment.payment_url}
                      sx={{ mb: 3, maxWidth: 520 }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleCopyPaymentLink} size="small">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('items')}</Typography>
                  <List dense sx={{ mb: 3 }}>
                    {(payment.items || []).map((item, i) => (
                      <ListItem key={i} disableGutters>
                        <ListItemText primary={item.description} secondary={`$${Number(item.amount).toFixed(2)}`} />
                      </ListItem>
                    ))}
                  </List>

                  {(['pending', 'expired', 'void'].includes(payment.status) && (payment.can_resend !== false) && !record.donation_made) && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                      <LoadingButton
                        variant="contained"
                        loading={isGeneratingPayment}
                        onClick={() => openPaymentDialog('resend')}
                        sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                      >
                        {t('resend_payment_link')}
                      </LoadingButton>
                    </Box>
                  )}

                  {payment.can_refund && (
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                      <LoadingButton
                        variant="outlined"
                        color="error"
                        loading={isRefunding}
                        onClick={openRefundDialog}
                      >
                        {t('refund_payment')}
                      </LoadingButton>
                    </Box>
                  )}

                  {(payment.status === 'refunded') && (
                    <LoadingButton
                      variant="contained"
                      loading={isGeneratingPayment}
                      onClick={() => openPaymentDialog('generate')}
                      sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                    >
                      {t('generate_new_payment_link')}
                    </LoadingButton>
                  )}
                </Box>
              )}
            </CardContent>
          </TabPanel>
        </Card>

        <Dialog
          open={previewDialog.open}
          onClose={() => setPreviewDialog({
            open: false, index: null, url: '', name: '', isImage: false, isPdf: false,
          })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pr: 1 }}>
            <Typography variant="h6" component="span" noWrap title={previewDialog.name}>
              {previewDialog.name}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setPreviewDialog({
                open: false, index: null, url: '', name: '', isImage: false, isPdf: false,
              })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa' }}>
            {previewDialog.isImage && previewDialog.url && (
              <Box
                component="img"
                src={previewDialog.url}
                alt={previewDialog.name}
                sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              />
            )}
            {previewDialog.isPdf && previewDialog.url && (
              <Box
                component="iframe"
                src={previewDialog.url}
                title={previewDialog.name}
                sx={{ width: '100%', height: '70vh', border: 0, bgcolor: '#fff' }}
              />
            )}
            {!previewDialog.isImage && !previewDialog.isPdf && previewDialog.url && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('preview')}
                </Typography>
                <Button
                  variant="contained"
                  href={previewDialog.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                >
                  {t('download')}
                </Button>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPreviewDialog({
                open: false, index: null, url: '', name: '', isImage: false, isPdf: false,
              })}
            >
              {t('close')}
            </Button>
            {previewDialog.index != null && (
              <Button
                variant="outlined"
                onClick={() => handleDownloadDocument(previewDialog.index)}
              >
                {t('download')}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={paymentConfirmOpen}
          onClose={() => !isGeneratingPayment && setPaymentConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {paymentDialogMode === 'resend' ? t('resend_payment_link') : t('generate_payment_link')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t(paymentDialogConfirmKey)}
            </DialogContentText>
            {record?.sibling_group_id && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('sibling_group')}: <strong>{record.sibling_group_id}</strong>
                </Typography>
                <List dense>
                  {(siblings.length > 0 ? siblings : [record]).map((r) => (
                    <ListItem key={r.hashid} disableGutters>
                      <ListItemText
                        primary={r.full_name}
                        secondary={`${toTitleCase(r.sacrament_type)} — ${r.email || t('no_email')}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
            {!record?.sibling_group_id && record && (
              <List dense>
                <ListItem disableGutters>
                  <ListItemText
                    primary={record.full_name}
                    secondary={`${toTitleCase(record.sacrament_type)} — ${record.email || t('no_email')}`}
                  />
                </ListItem>
              </List>
            )}
            <PaymentQuotePreview quote={paymentQuote} isLoading={isLoadingQuote} error={quoteError} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPaymentConfirmOpen(false)} disabled={isGeneratingPayment}>
              {t('cancel_action')}
            </Button>
            <LoadingButton
              variant="contained"
              loading={isGeneratingPayment || isLoadingQuote}
              disabled={isLoadingQuote || !paymentQuote || Number(paymentQuote?.total) <= 0 || Boolean(quoteError)}
              onClick={paymentDialogMode === 'resend' ? handleResendPayment : handleGeneratePayment}
              sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
            >
              {paymentDialogMode === 'resend' ? t('resend_payment_link') : t('generate_payment_link')}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteConfirm.open}
          onClose={() => !deletingDocIndex && setDeleteConfirm({ open: false, index: null, name: '' })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t('remove')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('confirm_delete_document', { name: deleteConfirm.name })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteConfirm({ open: false, index: null, name: '' })}
              disabled={deletingDocIndex != null}
            >
              {t('cancel_action')}
            </Button>
            <LoadingButton
              variant="contained"
              color="error"
              loading={deletingDocIndex != null}
              onClick={handleDeleteDocument}
            >
              {t('remove')}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={refundConfirmOpen}
          onClose={() => !isRefunding && setRefundConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('refund_payment')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('refund_payment_confirm')}
            </DialogContentText>
            {(() => {
              const { refundable, fee, net } = getRefundBreakdown();
              return (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('refundable_amount')}: <strong>${refundable.toFixed(2)}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {t('estimated_stripe_fee')}: <strong>${fee.toFixed(2)}</strong>
                    {' '}
                    ({Number(payment?.stripe_fee_percent || 2.9)}% + ${Number(payment?.stripe_fee_fixed || 0.3).toFixed(2)})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('net_refundable_amount')}: <strong>${net.toFixed(2)}</strong>
                  </Typography>
                </>
              );
            })()}
            <FormControlLabel
              sx={{ mb: 2, alignItems: 'flex-start' }}
              control={(
                <Checkbox
                  checked={deductStripeFee}
                  onChange={(e) => handleDeductFeeToggle(e.target.checked)}
                />
              )}
              label={(
                <Box>
                  <Typography variant="body2">{t('deduct_stripe_fee')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('deduct_stripe_fee_hint')}
                  </Typography>
                </Box>
              )}
            />
            <MoneyInput
              fullWidth
              size="small"
              label={t('refund_amount')}
              value={refundAmount}
              commitEmptyTo=""
              onChange={setRefundAmount}
              sx={{ mb: 2 }}
              helperText={
                deductStripeFee
                  ? t('refund_amount_net_helper')
                  : t('refund_amount_full_helper')
              }
            />
            <TextField
              fullWidth
              size="small"
              label={t('refund_reason')}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefundConfirmOpen(false)} disabled={isRefunding}>
              {t('cancel_action')}
            </Button>
            <LoadingButton
              variant="contained"
              color="error"
              loading={isRefunding}
              onClick={handleRefundPayment}
            >
              {t('confirm_refund')}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog open={navigationGuard.open || discardEditOpen} onClose={stayOnPage}>
          <DialogTitle>{t('unsaved_changes_title')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('unsaved_changes_body')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={stayOnPage}>{t('stay_on_page')}</Button>
            <Button color="warning" variant="contained" onClick={confirmLeave}>
              {t('leave_without_saving')}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
      </Page>
    </>
  );
}
